import React from 'react';
import SEO from '@/components/SEO';
import IdeaFactoryForm from '@/components/sections/ideafactory/IdeaFactoryForm';
import { Factory, Smartphone, Lightbulb } from 'lucide-react';
import { motion } from 'framer-motion';

const IdeaFactory = () => {
  return (
    <>
      <SEO 
        title="Idea Factory | Transformez vos idées en applications concrètes"
        description="Structurez votre idée d'application, obtenez des recommandations techniques et une estimation de prix grâce à notre outil d'aide à la conception. Créez votre MVP rapidement et efficacement."
        keywords="idée app, estimation prix app, conception application, développement logiciel, prototype application, MVP, innovation digitale, transformation numérique, création application"
        type="tool"
        url="https://orcsys.fr/idea-factory"
        image="https://orcsys.fr/images/idea-factory-preview.jpg"
      />
      <div className="min-h-[50vh] bg-gradient-to-br from-orcsys-50 via-white to-orcsys-50 flex flex-col items-center justify-center relative overflow-hidden pt-20 pb-10">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Idea Factory</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Transformez vos idées en projets concrets avec notre usine à idées interactive.
          </p>
        </div>
        
        {/* Animation of thought bubble entering factory and exiting as mobile phone */}
        <div className="w-full max-w-2xl mx-auto h-64 relative mb-8">
          {/* Factory in the middle */}
          <motion.div 
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 bg-white rounded-lg shadow-xl flex items-center justify-center z-20"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
          >
            <Factory className="w-16 h-16 text-orcsys-500" />
          </motion.div>
          
          {/* Conveyor belt */}
          <div className="absolute left-0 right-0 top-1/2 h-4 bg-gray-300 -translate-y-1/2 z-10"></div>
          
          {/* Thought bubble */}
          <motion.div 
            className="absolute left-0 top-1/2 -translate-y-1/2 w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center z-30"
            initial={{ x: -100, opacity: 0 }}
            animate={{ 
              x: ["-100%", "35%", "35%", "35%", "150%"],
              opacity: [0, 1, 1, 1, 0],
              y: [0, 0, -20, 0, 0],
              scale: [1, 1, 1.2, 1, 1]
            }}
            transition={{ 
              duration: 5, 
              repeat: Infinity, 
              repeatDelay: 1,
              times: [0, 0.3, 0.4, 0.5, 1]
            }}
          >
            <Lightbulb className="w-10 h-10 text-yellow-500" />
          </motion.div>
          
          {/* Mobile phone output */}
          <motion.div 
            className="absolute right-0 top-1/2 -translate-y-1/2 w-20 h-20 bg-orcsys-100 rounded-lg flex items-center justify-center border-2 border-orcsys-300 z-30"
            initial={{ x: 100, opacity: 0 }}
            animate={{ 
              x: ["100%", "-35%", "-35%", "-35%", "-150%"],
              opacity: [0, 0, 0, 1, 0],
              rotate: [0, 0, 0, 0, 10],
              scale: [0.8, 0.8, 0.8, 1.2, 1]
            }}
            transition={{ 
              duration: 5, 
              repeat: Infinity, 
              repeatDelay: 1, 
              times: [0, 0.5, 0.6, 0.7, 1] 
            }}
          >
            <Smartphone className="w-10 h-10 text-orcsys-500" />
          </motion.div>
          
          {/* Gear animations */}
          {[...Array(3)].map((_, index) => (
            <motion.div 
              key={index}
              className="absolute bg-gray-200 rounded-full flex items-center justify-center border-4 border-gray-300 z-15"
              style={{
                width: `${40 + index * 10}px`,
                height: `${40 + index * 10}px`,
                top: index % 2 === 0 ? '25%' : '65%',
                left: `${35 + index * 18}%`,
              }}
              animate={{ rotate: index % 2 === 0 ? 360 : -360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            >
              <div className="w-2 h-1/2 absolute bg-gray-400 rounded-full" style={{ transformOrigin: 'bottom center' }}></div>
              <div className="w-2 h-1/2 absolute bg-gray-400 rounded-full rotate-90" style={{ transformOrigin: 'bottom center' }}></div>
              <div className="w-2 h-1/2 absolute bg-gray-400 rounded-full rotate-45" style={{ transformOrigin: 'bottom center' }}></div>
              <div className="w-2 h-1/2 absolute bg-gray-400 rounded-full rotate-135" style={{ transformOrigin: 'bottom center' }}></div>
            </motion.div>
          ))}
          
          {/* Sparkles/particles */}
          {[...Array(10)].map((_, index) => (
            <motion.div 
              key={`particle-${index}`}
              className="absolute w-2 h-2 rounded-full bg-yellow-400 z-40"
              style={{
                top: '50%',
                left: '50%',
                originX: 0.5,
                originY: 0.5,
              }}
              animate={{ 
                x: [0, (Math.random() - 0.5) * 100],
                y: [0, (Math.random() - 0.5) * 100],
                opacity: [1, 0],
                scale: [0, 1, 0]
              }}
              transition={{ 
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "easeOut"
              }}
            />
          ))}
        </div>
        
        <div className="text-center mb-12">
          <p className="text-xl font-medium text-orcsys-600">
            De l'idée brute au produit fini
          </p>
        </div>
      </div>
      <IdeaFactoryForm />
    </>
  );
};

export default IdeaFactory;
